import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { Subject, takeUntil } from 'rxjs';
import { CustomConfigService } from '../../shared-services/custom-config.service';

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="hover-card" [style.backgroundColor]="data?.bgColor || '#f4f4f4'">
      <div class="stat-title">{{ data?.key }}</div>
      <div class="stat-value-row">
        <span *ngIf="data?.prefix" class="stat-symbol me-1">{{ data?.prefix }}</span>
        <span class="stat-number">{{ displayValue }}</span>
        <span *ngIf="data?.suffix" class="stat-symbol ms-1">{{ data?.suffix }}</span>
      </div>
    </div>
  `,
  styles: [`
    .hover-card {
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0px 0px;
      margin-top:0px;
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 100%;
      min-height: 100px;
      background-color: #f4f4f4;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      cursor: pointer;
    }
    .hover-card:hover {
      transform: translateY(-6px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }
    .stat-title {
      color: #000000;
      font-size: 15px;
      font-weight: 500;
      margin-bottom: 12px;
    }
    .stat-value-row {
      display: flex;
      align-items: baseline;
      justify-content: center;
    }
    .stat-number {
      font-size: 32px;
      font-weight: 700;
      color: #000000;
      line-height: 1;
    }
    .stat-symbol {
      font-size: 24px;
      font-weight: 600;
      color: #000000;
    }
  `]
})
export class NumberStatsComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @Input() filters?: any;

  public displayValue: string | number = '...';
  private readonly destroy$ = new Subject<void>();

  constructor(
    private configService: CustomConfigService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) { }

  ngOnInit(): void {
    this.fetchData();

    this.globalFilterService.filter$
      .pipe(takeUntil(this.destroy$))
      .subscribe((filters) => {
        if (!this.data?.widgetId) return;
        if (!this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) return;
        this.fetchData(filters);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected fetchData(incomingFilters?: any): void {
    if (!this.data?.apiUrl) return;

    const effectiveFilters = incomingFilters || this.filters || {};
    if (effectiveFilters?.legendSync) return;

    // Match bubble chart flow: pass global filters directly to custom config service.
    this.configService
      .getConfig(this.data.apiUrl, this.data, effectiveFilters,)
      .subscribe({
        next: (res: any) => {
          const widgetKey = this.data.key;
          let actualData = res;

          if (this.data.parentKey && res && res[this.data.parentKey]) {
            actualData = res[this.data.parentKey];
          } else if (res && res.data) {
            actualData = res.data;
          } else if (Array.isArray(res)) {
            actualData = res[0];
          }

          if (actualData && actualData[widgetKey] !== undefined) {
            const rawValue = actualData[widgetKey];
            this.displayValue = this.data.useFormatStat
              ? Number(rawValue).toFixed(2)
              : rawValue;
          } else {
            this.displayValue = '0.00';
            console.warn(`[NumberStats] Key "${widgetKey}" not found.`, actualData);
          }
        },
        error: (err) => {
          console.error('[NumberStats] API Error:', err);
          this.displayValue = '0.00';
        },
      });
  }
}

